import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAssortmentPlans } from '@/hooks/useAssortmentPlans'
import { useDepartments, useClasses, useSubclasses } from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { cn } from '@/utils/cn'
import {
  buildFinancialsRows,
  formatMetric,
  formatVariance,
  formatVariancePct,
  type FinancialsFilterState,
} from './financialsMockData'
import { FinancialsBreakdownTable } from './FinancialsBreakdownTable'

const ALL = '__all__'

const PLAN_STATUSES = [
  'Draft',
  'In Progress',
  'Submitted',
  'Approved',
  'Rejected',
  'Sent Back',
]

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export function FinancialsReportMockup() {
  const [planId, setPlanId] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)
  const [supplyChainCalendarId, setSupplyChainCalendarId] = useState<
    string | null
  >(null)
  const [seasonCalendarId, setSeasonCalendarId] = useState<string | null>(null)
  const [planStatus, setPlanStatus] = useState<string | null>(null)

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: plansResponse, isLoading: plansLoading } = useAssortmentPlans({
    pageSize: 100,
  })
  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)
  const { data: seasons, isLoading: seasonsLoading } = useSeasons()

  const filters = useMemo<FinancialsFilterState>(
    () => ({
      planId,
      departmentId,
      classId,
      subclassId,
      supplyChainCalendarId,
      seasonCalendarId,
      planStatus,
    }),
    [
      planId,
      departmentId,
      classId,
      subclassId,
      supplyChainCalendarId,
      seasonCalendarId,
      planStatus,
    ]
  )

  const rows = useMemo(() => buildFinancialsRows(filters), [filters])

  const plans = plansResponse?.plans ?? []

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Top strip */}
      <div className="flex items-start justify-between">
        <span className="text-xs italic text-destructive">
          Assortment Reports
        </span>
        <div className="text-right text-[11px] text-muted-foreground">
          <div>Last Update (TZ UTC-5)</div>
          <div>{lastUpdate}</div>
        </div>
      </div>

      {/* Plan Name — wide */}
      <FilterField label="Plan Name">
        <Select
          value={planId ?? ALL}
          onValueChange={v => setPlanId(v === ALL ? null : v)}
        >
          <SelectTrigger className="w-full bg-white">
            <SelectValue placeholder={plansLoading ? 'Loading...' : 'All'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All</SelectItem>
            {plans.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {/* 6 filter grid */}
      <div className="grid grid-cols-6 gap-4">
        <FilterField label="Department">
          <Select
            value={departmentId ?? ALL}
            onValueChange={v => {
              setDepartmentId(v === ALL ? null : v)
              setClassId(null)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={departmentsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {departments?.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Class">
          <Select
            value={classId ?? ALL}
            disabled={!departmentId}
            onValueChange={v => {
              setClassId(v === ALL ? null : v)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={classesLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {classes?.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Subclass">
          <Select
            value={subclassId ?? ALL}
            disabled={!classId}
            onValueChange={v => setSubclassId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={subclassesLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {subclasses?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Supply Chain Calendar">
          <Select
            value={supplyChainCalendarId ?? ALL}
            onValueChange={v => setSupplyChainCalendarId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={seasonsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {seasons?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Season Calendar">
          <Select
            value={seasonCalendarId ?? ALL}
            onValueChange={v => setSeasonCalendarId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={seasonsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {seasons?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Plan Status">
          <Select
            value={planStatus ?? ALL}
            onValueChange={v => setPlanStatus(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {PLAN_STATUSES.map(s => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      </div>

      {/* Tabs: Summary vs Breakdown */}
      <Tabs defaultValue="financials" className="mt-2">
        <TabsList>
          <TabsTrigger value="financials">Financials</TabsTrigger>
          <TabsTrigger value="breakdown">Financial Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="financials">
          <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[28%]" />
                  <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                    Planned
                  </TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                    Assortment
                  </TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                    $ Var to Plan
                  </TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                    % Var to Plan
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.label}>
                    <TableCell className="font-medium text-foreground">
                      {row.label}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMetric(row.planned, row.format)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMetric(row.assortment, row.format)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right tabular-nums',
                        row.variance < 0 && 'text-destructive'
                      )}
                    >
                      {formatVariance(row.variance, row.format)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right tabular-nums',
                        row.variancePct !== null &&
                          row.variancePct < 0 &&
                          'text-destructive'
                      )}
                    >
                      {formatVariancePct(row.variancePct)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="breakdown">
          <FinancialsBreakdownTable filters={filters} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
